import React from "react";
import { uniqueId } from "lodash";

import { IColum } from "../../../../utils/copyToClipboardInXlsFormat";
import { CopyToClipboard } from "../../../../components/ui";
import { IntlFormatUSD } from "../../../../utils";
import { CENTS_IN_DOLLAR } from "../../../../constants";
import { IGetTraderProfileResponse } from "../../../../api/fundedCompliance/index.types";
import EntriesIdWithPrefix, {
  getEntriesIdWithPrefix,
} from "../../../FundedCompliance/components/EntriesIdWithPrefix";

const calculateTotalPnL = (data: IGetTraderProfileResponse["data"]["pnl"][0]): number => {
  const { w20_sum = 0, w80_sum = 0, sum = 0, liveLosses_sum = 0 } = data;
  return (sum + w20_sum - w80_sum - liveLosses_sum) / 100;
};

type TWithHighlight = (withHighlight?: boolean) => IColum;

export const PNL_TABLE_COLUMNS_MAP: Record<string, TWithHighlight> = {
  entries: () => ({
    by: "entries",
    title: "Entries ID",
    path: "compliance_entries_id",
    getCellValue: (value) => <EntriesIdWithPrefix entriesId={value as unknown as number} />,
    copyToClipBoardCallBack: (value) => getEntriesIdWithPrefix(value as unknown as number),
  }),
  name: () => ({
    by: "name",
    title: "Name",
    path: "names",
    getCellValue: (value: unknown): React.ReactElement => {
      const names = Array.from(new Set(value as unknown as []));
      return (
        <>
          {names.map((v) => (
            <CopyToClipboard key={`${v}_${uniqueId()}`} value={v} />
          ))}
        </>
      );
    },
    // @ts-ignore
    copyToClipBoardCallBack: (value: string[]): string => {
      const names = Array.from(new Set(value as unknown as []));
      return names.join(", ");
    },
  }),
  sales: () => ({
    by: "sales",
    title: "Sales",
    path: "sum",
    getCellValue: (value) => (
      <CopyToClipboard value={IntlFormatUSD((value as unknown as number) / CENTS_IN_DOLLAR)} />
    ),
    copyToClipBoardCallBack: (value) =>
      IntlFormatUSD((value as unknown as number) / CENTS_IN_DOLLAR),
  }),
  w20: () => ({
    by: "20%",
    title: "20%",
    path: "w20_sum",
    getCellValue: (value) => (
      <CopyToClipboard value={IntlFormatUSD((value as unknown as number) / CENTS_IN_DOLLAR)} />
    ),
    copyToClipBoardCallBack: (value) =>
      IntlFormatUSD((value as unknown as number) / CENTS_IN_DOLLAR),
  }),
  w80: (withHighlight) => ({
    by: "80%",
    title: "80%",
    path: "w80_sum",
    getCellValue: (value) => {
      const w80 = (value as unknown as number) / CENTS_IN_DOLLAR;
      const component = <CopyToClipboard value={IntlFormatUSD(w80)} />;

      if (withHighlight && w80 >= 8000) {
        return (
          <div
            style={{
              backgroundColor: "rgb(212, 237, 188)",
              borderRadius: "8px",
              paddingBottom: "2px",
            }}
          >
            {component}
          </div>
        );
      }
      return component;
    },
    copyToClipBoardCallBack: (value) =>
      IntlFormatUSD((value as unknown as number) / CENTS_IN_DOLLAR),
  }),
  liveLoss: (withHighlight) => ({
    by: "liveLoss",
    title: "Live Loss",
    path: "liveLosses_sum",
    getCellValue: (value) => {
      const liveLoss = (value as unknown as number) / CENTS_IN_DOLLAR;
      const component = <CopyToClipboard value={IntlFormatUSD(liveLoss)} />;
      if (withHighlight && liveLoss >= 5000) {
        return (
          <div
            style={{
              backgroundColor: "rgb(255, 205, 210)",
              borderRadius: "8px",
              paddingBottom: "2px",
            }}
          >
            {component}
          </div>
        );
      }
      return component;
    },
    copyToClipBoardCallBack: (value) =>
      IntlFormatUSD((value as unknown as number) / CENTS_IN_DOLLAR),
  }),
  total_pnl: () => ({
    by: "total_pnl",
    title: "Total PnL",
    path: "total_pnl",
    // @ts-ignore
    getCellValue: (
      _: number,
      restValues: IGetTraderProfileResponse["data"]["pnl"][0]
    ): React.ReactElement => (
      <CopyToClipboard value={IntlFormatUSD(calculateTotalPnL(restValues))} />
    ),
    // @ts-ignore
    copyToClipBoardCallBack: (
      _: number,
      restValues: IGetTraderProfileResponse["data"]["pnl"][0]
    ): string => IntlFormatUSD(calculateTotalPnL(restValues)),
  }),
};

export const PNL_TABLE_COLUMNS: Array<IColum> = [
  PNL_TABLE_COLUMNS_MAP.entries(),
  PNL_TABLE_COLUMNS_MAP.name(),
  PNL_TABLE_COLUMNS_MAP.sales(),
  PNL_TABLE_COLUMNS_MAP.w20(),
  PNL_TABLE_COLUMNS_MAP.w80(),
  PNL_TABLE_COLUMNS_MAP.liveLoss(),
  PNL_TABLE_COLUMNS_MAP.total_pnl(),
];

export const COMPLIANCE_PROFILE_PNL_TABLE_COLUMNS: Array<IColum> = [
  PNL_TABLE_COLUMNS_MAP.entries(),
  PNL_TABLE_COLUMNS_MAP.name(),
  PNL_TABLE_COLUMNS_MAP.sales(),
  PNL_TABLE_COLUMNS_MAP.w20(),
  PNL_TABLE_COLUMNS_MAP.w80(true),
  PNL_TABLE_COLUMNS_MAP.liveLoss(true),
  PNL_TABLE_COLUMNS_MAP.total_pnl(),
];
